// =============================================================================
// Sino de avisos — só para o Financeiro
//
// Avisa quando uma solicitação de orçamento é aberta ou quando pendência nova
// chega à fila. O número vermelho é de NÃO LIDOS, e zera ao ler; é diferente
// do contador laranja da sidebar, que conta o que está EM ABERTO e só cai
// quando o item é tratado.
//
// Lido e não lido é estado por pessoa e mora no banco (v_aviso e
// rpc_marcar_avisos_lidos, do Enzo), não no navegador: a mesma pessoa em dois
// computadores veria dois sinos diferentes. Enquanto a migration não existe, a
// rota responde 503 e o sino simplesmente não aparece.
// =============================================================================

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buscarAvisos, marcarAvisosLidos, type Aviso } from "../lib/api.ts";
import { Icone } from "./Icone.tsx";

function quando(ts: string): string {
  return new Date(ts).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo",
  });
}

export function Sino({ onAbrir }: { onAbrir: (a: Aviso) => void }) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  const q = useQuery({
    queryKey: ["avisos"],
    queryFn: buscarAvisos,
    refetchInterval: 60_000,
    retry: false,
  });

  const marcar = useMutation({
    mutationFn: (chaves: string[] | null) => marcarAvisosLidos(chaves),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["avisos"] }),
  });

  // Fecha ao clicar fora e com Esc.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => { if (!caixa.current?.contains(e.target as Node)) setAberto(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setAberto(false); };
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", fora); document.removeEventListener("keydown", esc); };
  }, [aberto]);

  if (!q.data) return null;
  const avisos = q.data.avisos;
  const naoLidos = avisos.filter((a) => !a.lido).length;

  return (
    <div ref={caixa} className="relative ml-auto">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        aria-label={naoLidos ? `Avisos: ${naoLidos} não lidos` : "Avisos"}
        className="relative grid size-9 place-items-center rounded-[10px] border border-linha bg-superficie text-azul hover:border-linha-forte"
      >
        <Icone nome="sino" tamanho={18} espessura={2} />
        {naoLidos > 0 && (
          <span className="num absolute -top-1.5 -right-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-papel bg-risco-tinta px-1 text-[10px] font-bold text-white">
            {naoLidos > 99 ? "99+" : naoLidos}
          </span>
        )}
      </button>

      {aberto && (
        <div role="dialog" aria-label="Avisos"
          className="absolute top-[calc(100%+8px)] right-0 z-40 w-[360px] max-w-[calc(100vw-32px)] overflow-hidden rounded-xl border border-linha bg-superficie shadow-[0_14px_34px_rgb(12_31_62_/_0.2)]">
          <div className="flex items-center justify-between border-b border-linha px-4 py-3 text-[13px]">
            <strong>Avisos</strong>
            {naoLidos > 0 && (
              <button type="button" onClick={() => marcar.mutate(null)}
                className="text-[12px] font-semibold text-laranja-tinta hover:underline">
                Marcar todas como lidas
              </button>
            )}
          </div>
          <div className="thin-scroll max-h-[340px] overflow-y-auto">
            {avisos.length === 0 && <p className="px-4 py-5 text-[12.5px] text-tinta-fraca">Nenhum aviso.</p>}
            {avisos.map((a) => (
              <button key={a.chave} type="button"
                onClick={() => {
                  if (!a.lido) marcar.mutate([a.chave]);
                  setAberto(false);
                  onAbrir(a);
                }}
                className={`relative grid w-full gap-0.5 border-b border-linha py-2.5 pr-4 pl-7 text-left hover:bg-superficie-2 ${a.lido ? "bg-superficie" : "bg-[#fff8ef]"}`}>
                {!a.lido && <span aria-hidden className="absolute top-4 left-3 size-[7px] rounded-full bg-laranja" />}
                <span className={`text-[9.5px] font-bold tracking-[0.06em] uppercase ${a.tipo === "solicitacao" ? "text-laranja-tinta" : "text-azul-medio"}`}>
                  {a.tipo === "solicitacao" ? "Solicitação" : a.tipo === "ferramenta" ? "Ferramentas" : "Pendência"}
                </span>
                <span className="text-[13px] font-semibold">{a.titulo}</span>
                <span className="text-[12px] text-tinta-media">{a.texto}</span>
                <span className="text-[11px] text-tinta-fraca">{quando(a.criado_em)}</span>
              </button>
            ))}
          </div>
          <p className="bg-superficie-2 px-4 py-2.5 text-[11.5px] text-tinta-fraca">
            Avisa quando uma solicitação de orçamento é aberta ou quando pendências novas chegam à fila.
          </p>
        </div>
      )}
    </div>
  );
}
